import * as fs from 'node:fs';
import * as path from 'node:path';
import * as os from 'node:os';

// Canonical token-type classification from @wayai/core. The CLI now bundles
// @wayai/core (tsup `noExternal`), so this is a real runtime import — no copy.
import { identifyTokenType } from '@wayai/core';
export { identifyTokenType };
export type { TokenType } from '@wayai/core';

const KEYRING_SERVICE = 'wayai';
const KEY_WAY_TOKEN = 'way_token';
const KEY_OAUTH_ACCESS = 'oauth_access_token';
const KEY_OAUTH_REFRESH = 'oauth_refresh_token';

// Resolved per-call (not at module load) so tests can redirect via $HOME and
// production code picks up any runtime HOME changes (e.g., su -).
function configDir(): string { return path.join(os.homedir(), '.wayai'); }
function configPath(): string { return path.join(configDir(), 'config.json'); }

export type ResolvedToken =
  | { kind: 'way'; token: string }
  | { kind: 'oauth'; access_token: string; refresh_token?: string; expires_at?: string };

let keyringCache: KeyringBinding | null | undefined;
interface KeyringBinding {
  getPassword(service: string, account: string): Promise<string | null>;
  setPassword(service: string, account: string, password: string): Promise<void>;
  deletePassword(service: string, account: string): Promise<boolean>;
}

// Cross-platform manual cleanup hint. @napi-rs/keyring targets macOS Keychain,
// libsecret on Linux, and Windows Credential Manager — the shell command to
// evict a stuck entry differs per OS, so we tailor the message rather than
// printing a macOS-only `security` invocation to Linux/Windows users.
function manualCleanupCommand(account: string): string {
  if (process.platform === 'darwin') {
    return `security delete-generic-password -s ${KEYRING_SERVICE} -a ${account}`;
  }
  if (process.platform === 'win32') {
    return `cmdkey /delete:${KEYRING_SERVICE}/${account}`;
  }
  if (process.platform === 'linux') {
    return `secret-tool clear service ${KEYRING_SERVICE} account ${account}`;
  }
  return `(remove the entry with service="${KEYRING_SERVICE}" account="${account}" from your OS credential store)`;
}

/**
 * A keyring slot was deleted but a subsequent getPassword still returned a
 * value — the underlying secret store accepted the delete call without
 * actually removing the entry. Typically a macOS Keychain ACL surviving
 * from a prior login. Until the slot is cleared, writes to other slots
 * cannot reliably override what reads pick up first.
 */
export class StaleKeyringSlotError extends Error {
  constructor(public readonly account: string) {
    super(
      `Stale credential remains in OS keyring slot "${account}" after delete. ` +
      `This usually means a prior login left an ACL the current process can't override. ` +
      `Clean it up manually and retry:\n` +
      `  ${manualCleanupCommand(account)}`,
    );
    this.name = 'StaleKeyringSlotError';
  }
}

async function clearSlot(keyring: KeyringBinding, account: string): Promise<boolean> {
  await keyring.deletePassword(KEYRING_SERVICE, account);
  // A throw from getPassword (locked keychain, libsecret hiccup) means we
  // cannot confirm the slot is empty — treat the same as "stale entry
  // survived" so callers throw StaleKeyringSlotError rather than degrading
  // to file fallback (which would write OAuth alongside a possibly-stuck
  // way_, reproducing the masked-credential bug this helper exists to stop).
  try {
    return (await keyring.getPassword(KEYRING_SERVICE, account)) === null;
  } catch {
    return false;
  }
}

async function getKeyring(): Promise<KeyringBinding | null> {
  if (keyringCache !== undefined) return keyringCache;
  try {
    const mod = await import('@napi-rs/keyring');
    const Entry = mod.Entry;
    keyringCache = {
      async getPassword(service, account) {
        try { return new Entry(service, account).getPassword(); } catch { return null; }
      },
      async setPassword(service, account, password) {
        new Entry(service, account).setPassword(password);
      },
      async deletePassword(service, account) {
        try { return new Entry(service, account).deletePassword(); } catch { return false; }
      },
    };
  } catch {
    keyringCache = null;
  }
  return keyringCache;
}

interface LegacyConfigWithTokens {
  api_url?: string;
  token?: string;
  access_token?: string;
  refresh_token?: string;
  token_expires_at?: string;
}

function readLegacyConfigFile(): LegacyConfigWithTokens | null {
  if (!fs.existsSync(configPath())) return null;
  try {
    return JSON.parse(fs.readFileSync(configPath(), 'utf-8')) as LegacyConfigWithTokens;
  } catch {
    return null;
  }
}

function stripTokensFromFile(): void {
  const legacy = readLegacyConfigFile();
  if (!legacy) return;
  // token_expires_at is a timestamp, not a secret — keep it as metadata so the
  // refresh path can decide when to refresh without reading the keyring.
  const cleaned: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(legacy)) {
    if (k === 'token' || k === 'access_token' || k === 'refresh_token') continue;
    cleaned[k] = v;
  }
  fs.writeFileSync(configPath(), JSON.stringify(cleaned, null, 2) + '\n', { mode: 0o600 });
}

export async function migrateLegacyTokens(): Promise<ResolvedToken | null> {
  const legacy = readLegacyConfigFile();
  if (!legacy) return null;
  if (!legacy.token && !legacy.access_token) return null;

  const keyring = await getKeyring();

  if (legacy.token) {
    if (keyring) {
      try {
        await keyring.setPassword(KEYRING_SERVICE, KEY_WAY_TOKEN, legacy.token);
        // Strip failure leaves plaintext on disk until the next CLI invocation
        // re-attempts — keyring write was successful, so functionally OK.
        try { stripTokensFromFile(); } catch (err) {
          console.warn(`wayai: failed to strip legacy token from ~/.wayai/config.json (${(err as Error).message}); will retry next run.`);
        }
      } catch (err) {
        // Keyring write failed (locked keychain, etc.); leave the legacy file
        // intact so the file-fallback read keeps working and the next CLI run
        // can retry the migration.
        console.warn(`wayai: keyring migration failed (${(err as Error).message}); continuing with file-backed token.`);
      }
    }
    return { kind: 'way', token: legacy.token };
  }

  if (legacy.access_token) {
    if (keyring) {
      try {
        await keyring.setPassword(KEYRING_SERVICE, KEY_OAUTH_ACCESS, legacy.access_token);
        if (legacy.refresh_token) {
          await keyring.setPassword(KEYRING_SERVICE, KEY_OAUTH_REFRESH, legacy.refresh_token);
        }
        try { stripTokensFromFile(); } catch (err) {
          console.warn(`wayai: failed to strip legacy tokens from ~/.wayai/config.json (${(err as Error).message}); will retry next run.`);
        }
      } catch (err) {
        console.warn(`wayai: keyring migration failed (${(err as Error).message}); continuing with file-backed tokens.`);
      }
    }
    return {
      kind: 'oauth',
      access_token: legacy.access_token,
      refresh_token: legacy.refresh_token,
      expires_at: legacy.token_expires_at,
    };
  }

  return null;
}

/**
 * The `WAYAI_TOKEN` override, when the shell exports one. `getToken` returns it
 * before reading the keyring, so a command that reports on the STORED credential
 * must disclose it — otherwise it describes a credential the next command won't send.
 * Single source for the precedence rule: `login` and `logout` both read it here.
 */
export function envTokenOverride(): string | undefined {
  return process.env.WAYAI_TOKEN || undefined;
}

export async function getToken(): Promise<ResolvedToken | null> {
  // Env override has highest precedence — supports CI, container, op-run injection.
  const envToken = envTokenOverride();
  if (envToken) {
    if (identifyTokenType(envToken) === 'way_token') {
      return { kind: 'way', token: envToken };
    }
    return { kind: 'oauth', access_token: envToken };
  }

  const keyring = await getKeyring();

  if (keyring) {
    const wayToken = await keyring.getPassword(KEYRING_SERVICE, KEY_WAY_TOKEN);
    if (wayToken) return { kind: 'way', token: wayToken };

    const accessToken = await keyring.getPassword(KEYRING_SERVICE, KEY_OAUTH_ACCESS);
    if (accessToken) {
      const refreshToken = await keyring.getPassword(KEYRING_SERVICE, KEY_OAUTH_REFRESH);
      const expiresAt = readMetadataExpiresAt();
      return {
        kind: 'oauth',
        access_token: accessToken,
        refresh_token: refreshToken ?? undefined,
        expires_at: expiresAt ?? undefined,
      };
    }
  }

  const legacy = readLegacyConfigFile();
  if (!legacy) return null;
  if (legacy.token) return { kind: 'way', token: legacy.token };
  if (legacy.access_token) {
    return {
      kind: 'oauth',
      access_token: legacy.access_token,
      refresh_token: legacy.refresh_token,
      expires_at: legacy.token_expires_at,
    };
  }
  return null;
}

export async function setWayToken(token: string): Promise<void> {
  const keyring = await getKeyring();
  if (keyring) {
    try {
      await keyring.setPassword(KEYRING_SERVICE, KEY_WAY_TOKEN, token);
      clearMetadataExpiresAt();
      // Read precedence favors way_ over OAuth, so a leftover OAuth credential
      // won't shadow this token — but `wayai logout` then misses it. Warn so
      // the user can sweep it manually. Slots are independent — parallelize.
      const [accessCleared, refreshCleared] = await Promise.all([
        clearSlot(keyring, KEY_OAUTH_ACCESS),
        clearSlot(keyring, KEY_OAUTH_REFRESH),
      ]);
      const stuck = [
        accessCleared ? null : KEY_OAUTH_ACCESS,
        refreshCleared ? null : KEY_OAUTH_REFRESH,
      ].filter((a): a is string => a !== null);
      if (stuck.length > 0) {
        console.warn(
          [
            `wayai: stale OAuth credential persists in OS keyring after delete. Commands work, but \`wayai logout\` won't fully clear. Manually:`,
            ...stuck.map((a) => `  ${manualCleanupCommand(a)}`),
          ].join('\n'),
        );
      }
      return;
    } catch (err) {
      // Runtime keyring failure (locked keychain, broken libsecret session, etc.) —
      // degrade to file storage. Visible warning so users notice the downgrade.
      console.warn(`wayai: keyring write failed (${(err as Error).message}); falling back to file storage.`);
    }
  }
  writeFileFallback({ token });
}

export async function setOAuthTokens(
  access_token: string,
  refresh_token: string | undefined,
  expires_at: string,
): Promise<void> {
  const keyring = await getKeyring();
  if (keyring) {
    try {
      // way_ wins the read race, so a leftover way_ would silently mask the
      // OAuth tokens we're about to write — every subsequent API call would
      // send the dead way_ and the backend would reject it. Verify the slot
      // is actually empty after deletePassword and fail loudly otherwise.
      if (!(await clearSlot(keyring, KEY_WAY_TOKEN))) {
        throw new StaleKeyringSlotError(KEY_WAY_TOKEN);
      }
      await keyring.setPassword(KEYRING_SERVICE, KEY_OAUTH_ACCESS, access_token);
      if (refresh_token) {
        await keyring.setPassword(KEYRING_SERVICE, KEY_OAUTH_REFRESH, refresh_token);
      }
      writeMetadataExpiresAt(expires_at);
      return;
    } catch (err) {
      // StaleKeyringSlotError propagates — file fallback can't help because
      // the next read still hits the stuck keyring slot first.
      if (err instanceof StaleKeyringSlotError) throw err;
      console.warn(`wayai: keyring write failed (${(err as Error).message}); falling back to file storage.`);
    }
  }
  writeFileFallback({ access_token, refresh_token, token_expires_at: expires_at });
}

export async function clearTokens(): Promise<void> {
  const keyring = await getKeyring();
  if (keyring) {
    // Slots are independent — run all three in parallel (3 keyring RPCs vs 6).
    const slots = [KEY_WAY_TOKEN, KEY_OAUTH_ACCESS, KEY_OAUTH_REFRESH] as const;
    const cleared = await Promise.all(slots.map((account) => clearSlot(keyring, account)));
    for (const [i, ok] of cleared.entries()) {
      if (!ok) {
        const account = slots[i]!;
        console.warn(
          `wayai: could not clear keyring slot "${account}". Subsequent commands may still pick the stale token. Manually:\n` +
          `  ${manualCleanupCommand(account)}`,
        );
      }
    }
  }
  if (fs.existsSync(configPath())) {
    fs.unlinkSync(configPath());
  }
}

function readMetadataExpiresAt(): string | null {
  const legacy = readLegacyConfigFile();
  return legacy?.token_expires_at ?? null;
}

function writeMetadataExpiresAt(expires_at: string): void {
  const existing = readLegacyConfigFile() ?? {};
  delete existing.token;
  delete existing.access_token;
  delete existing.refresh_token;
  existing.token_expires_at = expires_at;
  ensureConfigDir();
  fs.writeFileSync(configPath(), JSON.stringify(existing, null, 2) + '\n', { mode: 0o600 });
}

function clearMetadataExpiresAt(): void {
  const existing = readLegacyConfigFile();
  if (!existing) return;
  // Strip any orphan token fields from prior file-fallback sessions while we're
  // here — keeps the metadata file token-free once keyring is in use.
  delete existing.token;
  delete existing.access_token;
  delete existing.refresh_token;
  delete existing.token_expires_at;
  ensureConfigDir();
  fs.writeFileSync(configPath(), JSON.stringify(existing, null, 2) + '\n', { mode: 0o600 });
}

function ensureConfigDir(): void {
  if (!fs.existsSync(configDir())) {
    fs.mkdirSync(configDir(), { recursive: true });
  }
}

function writeFileFallback(tokens: Partial<LegacyConfigWithTokens>): void {
  const existing = readLegacyConfigFile() ?? {};
  // Strip ALL token fields before merging — without this, a mode switch in the
  // no-keyring path (way → OAuth or vice versa) would leave the old mode's
  // fields on disk and `getToken` would return the stale value because the
  // file-fallback read checks `token` before `access_token`.
  delete existing.token;
  delete existing.access_token;
  delete existing.refresh_token;
  delete existing.token_expires_at;
  Object.assign(existing, tokens);
  ensureConfigDir();
  fs.writeFileSync(configPath(), JSON.stringify(existing, null, 2) + '\n', { mode: 0o600 });
}

/** Test hook — production code must not call this. */
export function __setKeyringForTesting(binding: KeyringBinding | null): void {
  keyringCache = binding;
}

/** Test hook — production code must not call this. */
export function __resetKeyringForTesting(): void {
  keyringCache = undefined;
}
